/**
 * ProductPage - Refactored with shared marketing components
 * Removed "NOT a CRM" duplication, added product evidence
 */

import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Check, Target, Zap, TrendingUp, Clock, Brain, BarChart3, AlertTriangle, MessageCircle, Phone, Upload, Settings } from "lucide-react";
import { ScrollReveal } from "@/components/landing/ScrollReveal";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { Section } from "@/components/marketing/Section";
import { Container } from "@/components/marketing/Container";
import { Split } from "@/components/marketing/Split";
import { FeatureGrid } from "@/components/marketing/FeatureGrid";
import { AppPreviewMock } from "@/components/marketing/AppPreviewMock";
import { ConversionCTA } from "@/components/marketing/ConversionCTA";

export default function ProductPage() {
  return (
    <MarketingLayout showBackLink>
      {/* Hero */}
      <Section spacing="lg">
        <Container size="lg">
          <ScrollReveal>
            <h1 className="text-[var(--font-display)] font-semibold tracking-[var(--tracking-tighter)] mb-6 leading-[var(--leading-display)]">
              The Product
            </h1>
            <p className="text-[var(--font-body-lg)] text-muted-foreground max-w-2xl leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">
              A decision-first operating system that replaces CRM chaos with daily execution clarity.
            </p>
          </ScrollReveal>
        </Container>
      </Section>

      {/* Today View */}
      <Section variant="secondary" spacing="lg">
        <Container size="xl">
          <Split gap="xl">
            <div>
              <ScrollReveal>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                  <Target className="w-4 h-4" />
                  Today View
                </div>
                <h2 className="text-[var(--font-h2)] font-semibold tracking-[var(--tracking-tighter)] mb-6 leading-[var(--leading-headline)]">
                  Your Daily Command Center
                </h2>
                <p className="text-[var(--font-body-lg)] text-muted-foreground mb-8 leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">
                  Every morning, open your Today view and know exactly who to contact, why they matter, and what action to take. No more scrolling through endless lists. No more guessing.
                </p>
                <ul className="space-y-4">
                  {[
                    "3-7 prioritized actions each day",
                    "Revenue impact score for each lead",
                    "One-click execution (call, WhatsApp, email)",
                    "Clear reasoning for every recommendation"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-foreground/80 leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">{item}</span>
                    </li>
                  ))}
                </ul>
              </ScrollReveal>
            </div>
            <ScrollReveal delay={0.2}>
              <AppPreviewMock />
            </ScrollReveal>
          </Split>
        </Container>
      </Section>

      {/* Decision Engine */}
      <Section spacing="lg">
        <Container size="xl">
          <Split reverse gap="xl">
            <ScrollReveal delay={0.2}>
              <div className="rounded-2xl border bg-card p-6 shadow-lg">
                <div className="text-center mb-8">
                  <Brain className="w-12 h-12 text-primary mx-auto mb-4" />
                  <h3 className="font-semibold text-lg tracking-[var(--tracking-tight)]">Decision Engine</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-secondary/50">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                      <Clock className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-sm">Time Decay</div>
                      <div className="text-xs text-muted-foreground">Last contact: 5 days ago</div>
                    </div>
                    <div className="text-primary font-semibold">+15</div>
                  </div>
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-secondary/50">
                    <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center text-success">
                      <TrendingUp className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-sm">Deal Value</div>
                      <div className="text-xs text-muted-foreground">$12,000 potential</div>
                    </div>
                    <div className="text-success font-semibold">+25</div>
                  </div>
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-secondary/50">
                    <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center text-warning">
                      <Zap className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-sm">Engagement</div>
                      <div className="text-xs text-muted-foreground">Opened last email</div>
                    </div>
                    <div className="text-warning font-semibold">+10</div>
                  </div>
                  <div className="border-t pt-4 mt-4">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">Priority Score</span>
                      <span className="text-2xl font-bold text-primary">87</span>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
            <div>
              <ScrollReveal>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-success/10 text-success text-sm font-medium mb-6">
                  <Brain className="w-4 h-4" />
                  Decision Engine
                </div>
                <h2 className="text-[var(--font-h2)] font-semibold tracking-[var(--tracking-tighter)] mb-6 leading-[var(--leading-headline)]">
                  Smart Prioritization, Not Just Data
                </h2>
                <p className="text-[var(--font-body-lg)] text-muted-foreground mb-8 leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">
                  Our decision engine analyzes multiple signals—time decay, deal value, engagement level, pipeline stage—to calculate a priority score for every lead, every day.
                </p>
                <ul className="space-y-4">
                  {[
                    "Multi-factor scoring algorithm",
                    "Real-time recalculation",
                    "Revenue-weighted priorities",
                    "Learns from your outcomes"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                      <span className="text-foreground/80 leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">{item}</span>
                    </li>
                  ))}
                </ul>
              </ScrollReveal>
            </div>
          </Split>
        </Container>
      </Section>

      {/* What You Actually Get - Product Evidence */}
      <Section variant="secondary" spacing="lg">
        <Container size="xl">
          <ScrollReveal>
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-[var(--font-h2)] font-semibold tracking-[var(--tracking-tighter)] mb-4 leading-[var(--leading-headline)]">
                What You Actually Get
              </h2>
              <p className="text-[var(--font-body-lg)] text-muted-foreground max-w-2xl mx-auto leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">
                Real features that drive execution, not just data storage.
              </p>
            </div>
          </ScrollReveal>
          
          <FeatureGrid cols={3} gap="lg">
            {[
              {
                icon: Target,
                title: "Locked Focus Mode",
                description: "One lead, one action. No distractions until you execute.",
              },
              {
                icon: MessageCircle,
                title: "WhatsApp Integration",
                description: "One-click WhatsApp actions with message templates.",
              },
              {
                icon: Phone,
                title: "Call Integration",
                description: "Direct calling from the app with automatic logging.",
              },
              {
                icon: Upload,
                title: "Meta Lead Ads",
                description: "Automatic lead import from Facebook Lead Ads.",
              },
              {
                icon: TrendingUp,
                title: "Auto-Scoring",
                description: "Every lead scored automatically on import.",
              },
              {
                icon: Clock,
                title: "Temperature Decay",
                description: "Lead temperature decreases over time automatically.",
              },
            ].map((feature, i) => {
              const Icon = feature.icon;
              return (
                <ScrollReveal key={i} delay={i * 0.1}>
                  <div className="p-6 rounded-xl border border-border/60 bg-card h-full flex flex-col hover:border-border/80 transition-colors">
                    <div className="p-2 rounded-lg bg-primary/10 border border-primary/20 w-fit mb-4">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2 tracking-[var(--tracking-tight)] leading-[var(--leading-headline)]">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-[var(--leading-body)] tracking-[var(--tracking-normal)] flex-grow">
                      {feature.description}
                    </p>
                  </div>
                </ScrollReveal>
              );
            })}
          </FeatureGrid>
        </Container>
      </Section>

      {/* Revenue Impact */}
      <Section spacing="lg">
        <Container size="xl">
          <Split gap="xl">
            <div>
              <ScrollReveal>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-warning/10 text-warning text-sm font-medium mb-6">
                  <BarChart3 className="w-4 h-4" />
                  Revenue Impact Logic
                </div>
                <h2 className="text-[var(--font-h2)] font-semibold tracking-[var(--tracking-tighter)] mb-6 leading-[var(--leading-headline)]">
                  Every Decision Tied to Revenue
                </h2>
                <p className="text-[var(--font-body-lg)] text-muted-foreground mb-8 leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">
                  We don't just track activities. We measure how every action impacts your pipeline. Know exactly how much revenue is at stake with each decision you make—or don't make.
                </p>
                <ul className="space-y-4">
                  {[
                    "Revenue-at-risk alerts",
                    "Deal velocity tracking",
                    "Conversion probability estimates",
                    "Opportunity cost visibility"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-warning mt-0.5 flex-shrink-0" />
                      <span className="text-foreground/80 leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">{item}</span>
                    </li>
                  ))}
                </ul>
              </ScrollReveal>
            </div>
            <ScrollReveal delay={0.2}>
              <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-6 shadow-lg">
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle className="w-4 h-4 text-destructive" />
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-destructive">
                    Revenue at Risk
                  </h3>
                </div>
                <div className="text-center mb-4">
                  <div className="text-3xl font-bold text-destructive mb-2">€12.50K</div>
                  <div className="text-xs text-muted-foreground">At Risk Today</div>
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  The system identifies who must be contacted to prevent revenue loss.
                </p>
              </div>
            </ScrollReveal>
          </Split>
        </Container>
      </Section>

      {/* Conversion CTA */}
      <ConversionCTA 
        headline="Ready to See It in Action?"
        bullets={[
          'Start your free trial and experience decision-first selling',
          'See how it transforms your daily workflow',
        ]}
      />
    </MarketingLayout>
  );
}

